import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS, scrollToId } from "../../lib/site";

export const Nav = () => {
  const isHomePage = window.location.pathname === "/";
  const [scrolled, setScrolled] = useState(() => !isHomePage || window.scrollY > 40);
  const [open, setOpen] = useState(false);

  const closeAndScroll = (target) => {
    setOpen(false);
    requestAnimationFrame(() => requestAnimationFrame(() => scrollToId(target)));
  };

  useEffect(() => {
    const onScroll = () => setScrolled(!isHomePage || window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHomePage]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-navy/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-nav"
    >
      <nav className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <button
          onClick={() => scrollToId("hero")}
          className="flex items-center shrink-0"
          data-testid="nav-logo"
          aria-label="SunShell One home"
        >
          <img
            src={LOGO_URL}
            alt="SunShell One"
            className={`h-8 sm:h-9 w-auto transition-[filter] duration-500 ${
              scrolled ? "" : "drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
            }`}
          />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => scrollToId(l.target)}
              data-testid={`nav-link-${l.target}`}
              className={`relative text-[0.95rem] font-medium tracking-tight group ${
                scrolled ? "text-navy/80" : "text-white/90"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[1.5px] w-0 bg-amber transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollToId("apps-download")}
            data-testid="nav-apps-btn"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber/25 transition-transform duration-300 hover:-translate-y-0.5 hover:bg-amber-600"
          >
            Get the apps
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </button>
          <button
            className={`md:hidden p-2 ${scrolled ? "text-navy" : "text-white"}`}
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-navy text-white md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-[70px]">
              <img src={LOGO_URL} alt="SunShell One" className="h-8 w-auto brightness-0 invert" />
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <div className="px-6 pt-10 flex flex-col gap-7">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                  onClick={() => closeAndScroll(l.target)}
                  className="text-left display-serif text-3xl"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => closeAndScroll("apps-download")}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber px-6 py-4 text-base font-semibold text-white"
              >
                Get the apps <ArrowUpRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
